import { z } from "zod";
import { zodToJsonSchema } from "zod-to-json-schema";
import { IsoDate, Method, Money, Ratio, SourceRef, StatedRange, Timestamp } from "./primitives.js";
/**
 * The typed facts Mosaic extracts from a separation's filings. Each is a discriminated
 * union member so `value` is correctly typed per kind rather than being `unknown`.
 */
export const FactKind = z.enum([
    "distribution_ratio",
    "record_date",
    "distribution_date",
    "when_issued_start",
    "spinco_debt",
    "parent_retained_stake_pct",
    "tax_treatment",
    "tsa_term_months",
    "spinco_ticker",
    "spinco_exchange",
    "employee_count",
    "segment_revenue",
    /**
     * The parent, named in prose. A `10-12B` is filed by the SpinCo, so the parent's CIK
     * is nowhere in the filing header; the name is the only identification the document
     * carries.
     */
    "parent_name",
    /**
     * The SpinCo, named in prose. A parent's announcement names a business rather than a
     * registrant, so this is how a situation opened from one acquires a name, and how two
     * separations by one parent are told apart.
     *
     * Not resolved to a CIK: a SpinCo has no EDGAR presence until it registers.
     */
    "spinco_name",
    /**
     * The parent's ticker, a key rather than a hint: "(Nasdaq: WDC)" identifies the parent
     * where a name has to be normalised to compare at all.
     */
    "parent_ticker",
    /**
     * The window the separation is expected to complete in, when a precise date is not
     * yet set: "in the second half of 2026". Deliberately not a date, so that it cannot be
     * mistaken for a real distribution date.
     */
    "expected_completion",
    /**
     * What each side does, in the filing's own words: a verbatim passage, never a
     * generated summary, because a paraphrase cannot be verified against the document.
     */
    "parent_business",
    "spinco_business",
    /**
     * The companies the filing itself names as competitors — never a peer set Mosaic
     * inferred, since choosing comparables is the owner's analysis.
     *
     * One fact holding a list, not one fact per company: a situation holds one live value
     * per kind, so per-company facts would supersede each other. The names come from one
     * passage and a later filing replaces them wholesale.
     *
     * SpinCo only: a Form 10's competition section is about the company being registered.
     */
    "spinco_peers",
]);
/**
 * A coarse period, the way a filing actually states one.
 *
 * `{ year: 2026, period: "H2" }` renders as the filing reads and sorts by its own start.
 * A stated period that fits none of these — "early 2027", "within 18 months" — is not
 * reported rather than rounded into the nearest bucket.
 */
export const CompletionWindow = z.object({
    year: z.number().int().min(1990).max(2100),
    period: z.enum(["Q1", "Q2", "Q3", "Q4", "H1", "H2", "FY"]),
});
/** The month a window opens, for ordering a radar by when things are expected. */
export function windowStartMonth(w) {
    return { Q1: 1, Q2: 4, Q3: 7, Q4: 10, H1: 1, H2: 7, FY: 1 }[w.period];
}
/**
 * A completion window as a filing would write it: `H2 2026`, or `2026` for a full year.
 *
 * @param w the window to render
 * @returns the display text
 */
export function formatWindow(w) {
    return w.period === "FY" ? String(w.year) : `${w.period} ${w.year}`;
}
/** A retained stake, as a percentage. Named so the range below reuses one definition. */
const RetainedPct = z.number().min(0).max(100);
/** A fact's value, tagged with its kind; each kind admits only the shape it can be stored as. */
export const FactValue = z.discriminatedUnion("kind", [
    /*
      A ratio or the span a filing contemplates. See `StatedRange`: a preliminary Form 10
      that names "1.0 to 3.0 shares" has stated a span, and the midpoint is a number the
      document does not contain.
    */
    z.object({ kind: z.literal("distribution_ratio"), value: z.union([Ratio, StatedRange(Ratio)]) }),
    z.object({ kind: z.literal("record_date"), value: IsoDate }),
    z.object({ kind: z.literal("distribution_date"), value: IsoDate }),
    z.object({ kind: z.literal("when_issued_start"), value: IsoDate }),
    z.object({ kind: z.literal("spinco_debt"), value: Money }),
    z.object({
        kind: z.literal("parent_retained_stake_pct"),
        value: z.union([RetainedPct, StatedRange(RetainedPct)]),
    }),
    z.object({
        kind: z.literal("tax_treatment"),
        value: z.enum(["section_355", "taxable", "rmt", "unknown"]),
    }),
    z.object({ kind: z.literal("tsa_term_months"), value: z.number().int().positive() }),
    z.object({ kind: z.literal("spinco_ticker"), value: z.string() }),
    z.object({ kind: z.literal("spinco_exchange"), value: z.string() }),
    z.object({ kind: z.literal("employee_count"), value: z.number().int().nonnegative() }),
    z.object({ kind: z.literal("segment_revenue"), value: Money }),
    z.object({ kind: z.literal("parent_name"), value: z.string().min(1) }),
    z.object({ kind: z.literal("spinco_name"), value: z.string().min(1) }),
    z.object({ kind: z.literal("parent_ticker"), value: z.string().min(1) }),
    z.object({ kind: z.literal("expected_completion"), value: CompletionWindow }),
    // The filing's own words, verbatim — see the note on the kinds above.
    z.object({ kind: z.literal("parent_business"), value: z.string().min(1) }),
    z.object({ kind: z.literal("spinco_business"), value: z.string().min(1) }),
    /*
      At least one name, because an empty list and no fact at all are the same claim and
      only one of them should be storable. A filing that names no competitors reports
      nothing, exactly as it does for any other absent fact.
    */
    z.object({ kind: z.literal("spinco_peers"), value: z.array(z.string().min(1)).min(1) }),
]);
/**
 * A stored fact. Facts are append-only: a correction inserts a new row and sets
 * `supersededBy` on the old one.
 */
export const ExtractedFact = z.object({
    id: z.string().uuid(),
    situationId: z.string().uuid(),
    fact: FactValue,
    source: SourceRef,
    method: Method,
    confidence: z.number().min(0).max(1),
    /** The filing marked this assumed. Never projected, never advances the stage. */
    tentative: z.boolean(),
    extractedAt: Timestamp,
    supersededBy: z.string().uuid().nullable(),
    /** Present only on `method: "user"` rows — why the owner overrode the extraction. */
    note: z.string().nullable(),
});
/**
 * The shapes a fact value can take in the model's output, before it is narrowed to its
 * kind.
 *
 * Not `z.unknown()`: that serialises to a JSON Schema with no `type`, which the SDK
 * rejects. Enumerating the shapes also constrains generation to what can be stored.
 */
export const FactValueShape = z.union([
    // distribution_ratio
    z.object({ numerator: z.number().int(), denominator: z.number().int() }),
    // spinco_debt, segment_revenue — decimal string, never a float
    z.object({ amount: z.string(), currency: z.string() }),
    // dates, tickers, exchanges, tax_treatment
    z.string(),
    // parent_retained_stake_pct, tsa_term_months, employee_count
    z.number(),
    // expected_completion
    z.object({
        year: z.number().int(),
        period: z.enum(["Q1", "Q2", "Q3", "Q4", "H1", "H2", "FY"]),
    }),
    // spinco_peers — the companies one passage names, as one claim
    z.array(z.string()),
    /*
     * A span, for the two kinds whose `FactValue` accepts one. Every shape `FactValue`
     * accepts must appear here, or an answer that states it correctly fails to parse.
     */
    StatedRange(z.object({ numerator: z.number().int(), denominator: z.number().int() })),
    StatedRange(z.number()),
]);
/**
 * One fact as the model proposes it, with the quote it rests on; nothing is stored until the
 * quote is located and the value narrowed to its kind.
 */
export const ExtractionCandidateSchema = z.object({
    kind: FactKind,
    /** Narrowed to the concrete kind's type at verification time. */
    value: FactValueShape,
    quote: z.string().min(1),
    confidence: z.number().min(0).max(1),
    reasoning: z.string(),
    /**
     * The filing offers this as assumed rather than stating it ("an assumed Distribution
     * ratio of 1-for-4").
     *
     * Distinct from `confidence`, which is the model's certainty about its own reading:
     * here the uncertainty is the filing's.
     *
     * Optional so answers stored in `extraction_runs` without it still parse.
     */
    tentative: z.boolean().optional(),
});
/**
 * Whether the document describes a separation at all, as the reader states it.
 *
 * `false` must be stated, never inferred from an empty `facts` array: a separation
 * announced last week has no terms either.
 */
export const SeparationVerdict = z.object({
    isSeparation: z.boolean(),
    /** Why — shown to the owner verbatim, and written into the situation event. */
    reason: z.string().min(1).max(2000),
    /**
     * The sentence it rests on, verified like any other quote when present. Nullable
     * because a verdict of `false` often rests on absence and has nothing to quote.
     */
    quote: z.string().nullable().optional(),
});
/**
 * One entity the document is about, established once and carried through every later
 * pass of a multi-pass read.
 *
 * A window in the middle of a filing cannot tell which company a figure belongs to, and
 * quote verification cannot catch a verbatim figure attached to the wrong entity. The
 * roster tells it, and each entry carries the sentence that establishes it.
 */
export const RosterEntry = z.object({
    name: z.string().min(1),
    /**
     * `other` is a managed company, a seller or a consolidated affiliate whose figures sit
     * in the same filing and belong to neither side of the separation.
     */
    role: z.enum(["parent", "spinco", "other"]),
    /** What the document says that establishes this. Verified like any other quote. */
    quote: z.string().min(1),
    /**
     * Every other string this document uses for the same entity, including defined terms.
     * One filing can define "the Company" differently in its front matter and in its
     * financial-statement notes.
     */
    aliases: z.array(z.string().min(1)).optional(),
    /** Why this entity is not one of the two sides, where that is the point of the entry. */
    note: z.string().optional(),
});
/**
 * What one window pass of a multi-pass read may return.
 *
 * There is deliberately no `notFound` and no `separation`: a pass that has seen one
 * window cannot know a term is absent from the document. It can only add entities,
 * candidates and open questions; absence is the reconciliation pass's to state, once
 * every window has been read.
 */
export const WindowPassOutput = z.object({
    /** Entities this window establishes that the roster did not already carry. */
    roster: z.array(RosterEntry),
    facts: z.array(ExtractionCandidateSchema),
    /**
     * What this window raised and could not settle — "a ratio appears here but the sentence
     * calls it assumed". Carried forward to later windows and the reconciliation pass.
     */
    openQuestions: z.array(z.string()),
});
/**
 * `WindowPassOutput` as self-contained JSON Schema, for a window pass's `output_config.format`.
 *
 * @returns the schema, with no `$ref`s
 */
export function windowPassJsonSchema() {
    return zodToJsonSchema(WindowPassOutput, {
        target: "jsonSchema7",
        $refStrategy: "none",
    });
}
/** The answer to a single-pass read, or to the reconciliation pass of a multi-pass read. */
export const ExtractionOutput = z.object({
    facts: z.array(ExtractionCandidateSchema),
    /** Kinds the document does not establish. Recorded so absence is visible, not implied. */
    notFound: z.array(z.string()),
    separation: SeparationVerdict.optional(),
});
/**
 * The extraction schema as JSON Schema, for `output_config.format`. Derived from
 * `ExtractionOutput`, so it cannot drift from `FactKind`.
 */
export function extractionJsonSchema() {
    const schema = zodToJsonSchema(ExtractionOutput, {
        target: "jsonSchema7",
        $refStrategy: "none", // the API wants a self-contained schema, not $refs
    });
    return schema;
}
/**
 * One term a filing stated or changed, read from the `supersededBy` chain rather than
 * from extraction order, which says when Mosaic looked rather than what the filing
 * changed.
 */
export const FilingTermChange = z.object({
    kind: FactKind,
    value: z.unknown(),
    tentative: z.boolean(),
    method: Method,
    quote: z.string().nullable(),
    documentId: z.string().uuid().nullable(),
    charStart: z.number().int().nullable(),
    /**
     * The value this one replaced, or null when the filing stated the term first. Wrapped
     * in an object so a replaced value of `null` is distinguishable from no replacement.
     */
    previous: z.object({ value: z.unknown(), tentative: z.boolean() }).nullable(),
});
//# sourceMappingURL=fact.js.map